/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * b2b-cart-batch.js — ONE cart mutation per BURST of edits, on both cart surfaces (D288.6).
 *
 * 🔴 THE DEFECT THIS CLOSES. Focal's `line-item-quantity` sends one `/cart/change.js` per step:
 * every tap on « + », every typed quantity, is its own round-trip, and on the CART PAGE it then
 * calls `window.location.reload()` — a full page load per line edit. On a wholesale basket of
 * 100+ lines that is the difference between adjusting an order and abandoning it. The operator
 * proposed a « Valider les quantités » button that would reload the page, assuming a reload was
 * the only way to repaint. It is not: `/cart/update.js` takes a WHOLE key→quantity map in one
 * request and accepts the same `sections` parameter the add path already uses, so one request can
 * apply every edit AND return the repainted markup for both the drawer and the cart page.
 *
 * 🔴 WHY A DEBOUNCE AND NOT A NEW BUTTON. « Valider les quantités » is a new affordance, and CD
 * never drew one — the standing rule on this build is converge to CD, never invent. The mechanism
 * the operator actually asked for is « hold the edits, send them once », which a coalescing queue
 * delivers with no new control, no reload and no lost scroll position. The one thing a button
 * would have given for free is the guarantee that nobody checks out on quantities that have not
 * been sent yet; that is reproduced explicitly below by disabling checkout while edits are
 * pending, so the guarantee survives without the control.
 *
 * 🔴 KEYS, NEVER LINE INDICES. `/cart/update.js` accepts a positional array too, and using it here
 * would be a defect: removing or zeroing a line renumbers every line after it, and the cart page
 * renders its lines GROUPED, so the posted order is not the cart's order. Both B2B surfaces
 * already render `name="updates[<line_item.key>]"` for exactly this reason. Any input that does
 * NOT carry a key (Focal's native `updates[]` markup, still present in the non-B2B fallback
 * blocks) is deliberately left to Focal — this file never guesses a key from a position.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.WajB2bCart) return;

  /* Two idle windows, because "still in the cart" and "gone from the cart" are different questions.
     🔴 IN_CART IS BOUNDED, NOT INFINITE. An earlier draft simply refused to flush while focus was
     inside a stepper — which batches beautifully and STRANDS the edits if the buyer never blurs:
     type a quantity, hit back, and the basket never heard about it. 2,5 s is longer than the pace
     of someone working down a basket (so ten lines are still one request) and short enough that a
     genuine pause commits. Stranding is the worse failure, and it is silent. */
  var IDLE_IN_CART_MS = 2500;
  var IDLE_MS = 700;

  var pending = {};      // line key → quantity, coalesced: the LAST value typed wins
  var timer = null;
  var chain = Promise.resolve();
  var inFlight = 0;      // requests currently open — the ONLY thing that disables checkout
  var seq = 0;           // monotonic: a repaint from an older flush must never win
  var submitting = false;

  function routes() { return (window.themeVariables && window.themeVariables.routes) || {}; }
  function cartUrl() { return routes().cartUrl || '/cart'; }
  function updateUrl() { return cartUrl() + '/update.js'; }

  /* The cart page's section id is read from the markup rather than assumed to be « main »: a JSON
     template's section key is merchant-editable, and a hardcoded id would silently stop repainting
     the day it changes — with a stale price list on screen, which is the one failure that matters
     on a wholesale cart. Absent attribute ⇒ we are not on the cart page. */
  function cartPageSection() {
    var host = document.querySelector('[data-b2b-cart-section]');
    return host ? host.getAttribute('data-b2b-cart-section') : null;
  }

  function sectionsWanted() {
    var list = [];
    if (document.querySelector('cart-drawer')) list.push('mini-cart');
    var id = cartPageSection();
    if (id) list.push(id);
    return list;
  }

  /* Checkout is disabled for as long as an edit has been typed but not yet applied. This is the
     « Valider » guarantee without the button: the buyer cannot reach checkout on a basket that
     does not yet hold what is on their screen. Every repaint replaces the footer wholesale, so the
     button comes back enabled by construction — nothing here has to remember to re-enable it. */
  /* 🔴 CHECKOUT IS DISABLED ONLY WHILE A REQUEST IS ACTUALLY OPEN — not while edits merely sit in
     the queue (2026-08-29). The first version disabled it for the whole pending window, so a buyer
     working down a basket found the button dead and no way to say « I am done ». Typed-but-unsent
     edits are handled by INTERCEPTING the checkout click instead: flush, wait, then go. The
     guarantee is the same — nobody checks out on quantities the basket does not hold — and the
     control stays alive. `waj-cart-pending` still marks the pending window for CSS. */
  function setBusy(state) {
    Array.prototype.forEach.call(document.querySelectorAll('[name="checkout"]'), function (btn) {
      btn.disabled = state;
      btn.setAttribute('aria-busy', state ? 'true' : 'false');
    });
  }

  function markPending() {
    document.documentElement.classList.toggle('waj-cart-pending', hasPending());
  }

  function hasPending() {
    return Object.keys(pending).length > 0 || timer !== null;
  }

  /* Is the buyer still working inside the cart? A debounce cannot tell « paused to think » from
     « finished », so it does not try: while focus is in a stepper the queue simply keeps
     accumulating, and the flush happens when they leave the cart or head for checkout. That is what
     turns ten edited lines into ONE request instead of one per line at human typing speed. */
  function focusInCart() {
    var el = document.activeElement;
    return !!(el && el.closest && el.closest('#mini-cart, .b2bc'));
  }

  /* Which input currently has the caret, by line key — so a repaint can put it back. Losing focus
     mid-basket is the difference between "it updated" and "it fought me". */
  function focusedKey() {
    var el = document.activeElement;
    if (!el || !el.matches || !el.matches('input[name^="updates["]')) return null;
    return keyOf(el);
  }

  /* 🔴 EVERY TYPED VALUE THE SERVER HAS NOT SEEN YET MUST SURVIVE THE REPAINT (2026-08-29).
     The repaint replaces the section's whole innerHTML with server-rendered markup, so any quantity
     the buyer typed into ANOTHER line while a flush was in the air is silently reverted — they watch
     their own entry disappear. Re-applying `pending` afterwards is what makes editing several lines
     in a row possible at all. */
  function restore(key, caret) {
    Object.keys(pending).forEach(function (k) {
      var input = document.querySelector('input[name="updates[' + (window.CSS && CSS.escape ? CSS.escape(k) : k) + ']"]');
      if (input) input.value = pending[k];
    });
    if (!key) return;
    var back = document.querySelector('input[name="updates[' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + ']"]');
    if (!back) return;
    try {
      back.focus();
      if (caret != null && back.setSelectionRange) back.setSelectionRange(caret, caret);
    } catch (e) { /* a number input can refuse setSelectionRange; focus is the part that matters */ }
  }

  function repaint(cart) {
    var sections = (cart && cart.sections) || null;
    if (!sections) {
      /* No markup came back — re-render from the basket rather than leave the screen lying. */
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    /* The drawer repaints through Focal's own path: `CartDrawer._rerenderCart` reads
       `detail.cart.sections['mini-cart']` and preserves the scroll position. `cart:updated` is a
       separate event with separate listeners (the header count, the free-shipping bar), so both
       are dispatched — one is not a substitute for the other. */
    if (sections['mini-cart']) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, replacementDelay: 0 } }));
    }

    var id = cartPageSection();
    if (id && sections[id]) {
      var host = document.getElementById('shopify-section-' + id);
      if (host) {
        var key = focusedKey();
        var el = document.activeElement;
        var caret = (el && el.selectionStart != null) ? el.selectionStart : null;
        var parsed = document.createElement('div');
        parsed.innerHTML = sections[id];
        var inner = parsed.querySelector('#shopify-section-' + id);
        var scroller = document.scrollingElement || document.documentElement;
        var y = scroller.scrollTop;
        host.innerHTML = inner ? inner.innerHTML : parsed.innerHTML;
        scroller.scrollTop = y;
        restore(key, caret);
      }
    }
  }

  /* One request, whatever the burst was. Serialised through `chain` because the cart is ONE
     server-side resource: two overlapping `/cart/update.js` calls race, and the loser's map is
     applied to a basket that has already moved. */
  function send(updates, mySeq) {
    var sections = sectionsWanted();
    var body = { updates: updates };
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    return fetch(updateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart update failed');
        return r.json();
      })
      .then(function (cart) {
        /* 🔴 TWO REASONS TO SWALLOW A REPAINT, both measured on the dev theme 2026-08-29.
           (a) STALE ORDER — an older flush's response can land after a newer edit has been typed,
               and repainting from it shows the buyer a number they already changed. `seq` is
               monotonic, so only the newest flush is allowed to paint.
           (b) MORE EDITS ARE ALREADY QUEUED — repainting now would yank the DOM out from under
               someone still typing, for markup that is about to be superseded anyway. The flush
               that drains the queue will paint. */
        if (mySeq !== seq) return cart;
        if (Object.keys(pending).length) return cart;
        repaint(cart);
        return cart;
      })
      .catch(function () {
        /* A failed update leaves the screen showing quantities the basket does not hold. Re-render
           from the basket so the buyer sees the truth, not their typed intention. */
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
  }

  function flush() {
    if (timer) { window.clearTimeout(timer); timer = null; }
    var updates = pending;
    pending = {};
    markPending();
    if (!Object.keys(updates).length) return chain;
    inFlight = inFlight + 1;
    setBusy(true);
    seq = seq + 1;
    var mySeq = seq;
    chain = chain.then(function () { return send(updates, mySeq); }).then(function (cart) {
      inFlight = inFlight - 1;
      if (inFlight <= 0) { inFlight = 0; setBusy(false); }
      markPending();
      return cart;
    });
    return chain;
  }

  /* The timer re-arms rather than firing while the buyer is still in the cart. Without this, a
     realistic pace — a second or two per line — produces one request per line, which is what the
     operator hit: « doesn't handle changing quantities of multiple line items ». */
  function arm() {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      timer = null;
      flush();
    }, focusInCart() ? IDLE_IN_CART_MS : IDLE_MS);
  }

  /* 🔴 THE STEPPER'S OWN STATE, WRITTEN IMMEDIATELY (2026-08-30). `data-b2b-qty` is what the CSS
     reads to choose between « − » and the trash, and it used to change only when the server
     re-rendered the row — up to 2,5 s later now that edits are batched, during which a qty-1 line
     still showed an inert « − ». Rewriting it here makes the swap instant in both directions, with
     no round-trip; the next repaint just re-seeds the same value from the server. */
  function markQty(input, quantity) {
    var stepper = input.closest('[data-b2b-qty]');
    if (stepper) stepper.setAttribute('data-b2b-qty', String(quantity));
  }

  function queue(key, quantity) {
    if (!key) return;
    pending[key] = quantity;
    markPending();
    arm();
  }

  /* An immediate, un-debounced map — used by the removal paths, where the gesture is deliberate
     and the collapse animation has already played. Anything still queued goes with it, so a
     removal never overtakes a quantity the buyer typed a moment earlier. */
  function update(updates) {
    Object.keys(updates || {}).forEach(function (k) { pending[k] = updates[k]; });
    return flush();
  }

  // ── interception ──────────────────────────────────────────────────────────────────────────────
  // Capture phase on `document`, so this runs before `line-item-quantity`'s own delegates (which
  // are bound to the element, i.e. bubble phase) and can stop the event reaching them. Anything
  // this file declines to handle propagates untouched and Focal behaves exactly as before.

  function keyOf(input) {
    var m = /^updates\[(.+)\]$/.exec(input.getAttribute('name') || '');
    return m ? m[1] : null;    // `updates[]` → null → not ours, Focal keeps it
  }

  /* 🔴 THE STEPPER'S FLOOR IS 1, NOT 0 — caught in render QA, 2026-08-29, and it is a regression
     this file would otherwise have introduced. Un-batched, the « − » link could never reach 0: its
     href is rebuilt on every re-render with `at_least: quantity_rule.min`, so from 1 it pointed at
     1. Stepping from a cached href removes that guard, and two taps on « − » silently DELETED a
     line — a destructive gesture the buyer never asked for, with no collapse animation and no
     confirmation. Removal stays the ✕'s job (and, in the drawer, the qty-1 trash button, which is
     a separate control this path never sees). Absent a `min` attribute the floor is 1, which is
     what Focal's own `InputNumber` assumes.
     A quantity of 0 still reaches `/cart/update.js` — but only through `update()`, the removal
     path, which does not pass through here. */
  function clamp(input, quantity) {
    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    if (isNaN(min)) min = 1;
    if (!isNaN(max) && quantity > max) quantity = max;
    if (quantity < min) quantity = min;
    return quantity;
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var link = event.target.closest('line-item-quantity a[href]');
    if (!link) return;
    var wrap = link.closest('line-item-quantity');
    var input = wrap && wrap.querySelector('input[name^="updates["]');
    if (!input) return;
    var key = keyOf(input);
    if (!key) return;

    var target;
    try {
      target = parseInt(new URL(link.href, window.location.origin).searchParams.get('quantity'), 10);
    } catch (e) { return; }
    if (isNaN(target)) return;

    /* 🔴 THE HREF IS A RENDER-TIME CONSTANT, NOT A COMMAND — caught in render QA, 2026-08-29.
       Focal writes the DESTINATION quantity into the link (`?quantity={{ line_item.quantity | plus:
       increment }}`), which is only ever « what this line held when the page was drawn, plus one ».
       That worked because every tap re-rendered the row and rewrote the href. Batching removes the
       re-render, so four taps on « + » all carried `quantity=3` and the line went 2 → 3, not 2 → 6:
       the buyer taps and nothing moves. Measured, not reasoned about.
       So the link is read for its STEP, never its target: the step is the href's quantity minus the
       value the SERVER rendered into the input (`getAttribute('value')`, which survives every
       property write we make), and it is applied to whatever the input currently holds. A step of 0
       means the link was clamped at its bound at render time and the control is inert either way,
       so the href stands in as the target. */
    var rendered = parseInt(input.getAttribute('value'), 10);
    var current = parseInt(input.value, 10);
    var qty = target;
    if (!isNaN(rendered) && !isNaN(current)) {
      var step = target - rendered;
      if (step !== 0) qty = current + step;
    }

    event.preventDefault();
    event.stopPropagation();

    qty = clamp(input, qty);
    input.value = qty;
    input.setAttribute('size', String(String(qty).length < 2 ? 2 : String(qty).length));
    markQty(input, qty);
    queue(key, qty);
  }, true);

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.closest || !input.matches || !input.matches('input[name^="updates["]')) return;
    if (!input.closest('line-item-quantity')) return;
    var key = keyOf(input);
    if (!key) return;

    /* `InputNumber` clamps on `input` (which has already fired) and only handles the EMPTY case on
       `change` — the handler this stopPropagation is about to suppress. So that one case is
       reproduced here rather than lost. */
    if (input.value === '') input.value = input.getAttribute('min') || '1';

    var qty = parseInt(input.value, 10);
    if (isNaN(qty)) return;

    event.stopPropagation();
    qty = clamp(input, qty);
    input.value = qty;
    markQty(input, qty);
    queue(key, qty);
  }, true);

  /* Last chance to send: a burst still sitting in the queue when the buyer leaves the drawer, the
     field, or the tab would otherwise be lost silently. `visibilitychange` rather than
     `beforeunload`, which does not fire reliably and cannot be awaited either way. */
  /* 🔴 TABBING BETWEEN TWO LINES IS NOT "DONE EDITING" (2026-08-29, operator: « doesn't handle
     changing quantities of multiple line items »). This used to flush on ANY focusout inside a
     stepper, so moving from one size to the next fired its own request — one round-trip per line,
     which is the very behaviour the batching exists to remove, plus a repaint landing on the line
     the buyer had just started typing into. It now flushes only when focus leaves the cart surface
     altogether; `relatedTarget` is where focus is GOING, and null (a click on nothing, or the page
     losing focus) counts as leaving. */
  document.addEventListener('focusout', function (event) {
    if (!event.target || !event.target.closest || !event.target.closest('line-item-quantity')) return;
    var to = event.relatedTarget;
    if (to && to.closest && to.closest('#mini-cart, .b2bc')) return;
    flush();
  }, true);
  document.addEventListener('visibilitychange', function () { if (document.hidden) flush(); });

  /* 🔴 THE LAST-CHANCE SEND. A queued edit that has not left the browser when the page does is a
     quantity the buyer typed, saw on screen, and does not have. `pagehide` fires where
     `beforeunload` is unreliable, and nothing can be awaited at that point — so this is a one-shot
     `keepalive` POST, deliberately without `sections` (there is no page left to repaint) and with
     no error handling, because there is nowhere to report to. Best effort, and far better than the
     silent loss it replaces. */
  window.addEventListener('pagehide', function () {
    if (!Object.keys(pending).length) return;
    var updates = pending;
    pending = {};
    try {
      fetch(updateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
        keepalive: true
      });
    } catch (e) { /* the page is going away; there is no recovery to attempt */ }
  });
  document.documentElement.addEventListener('cart-drawer:close', flush);

  // ── 42c / CD-119 + 40b — REMOVAL, both surfaces ───────────────────────────────────────────────
  // 🔴 MOVED HERE FROM `sections/mini-cart.liquid` (2026-08-29) because THE MINI-CART SECTION IS
  // NOT RENDERED ON THE CART PAGE. Measured on the dev theme: `/cart` has no `#mini-cart` and no
  // `<cart-drawer>`, so the inline script that carried this delegate simply was not on the page —
  // and the cart page's new product-level ✕ was dead on arrival with a perfectly correct selector.
  // A delegate shared by two surfaces has to live somewhere both of them load, which on this theme
  // means an asset in `layout/theme.liquid`. Class names stay per-surface (`b2bm__` drawer,
  // `b2bc__` page) because the geometry genuinely differs; only the BEHAVIOUR is shared.

  var COLLAPSE_MS = 180;

  /* Pin the measured height, force a reflow so the browser has a start value to animate FROM, then
     drive it to 0. `display` cannot be transitioned, which is why this is a height animation and
     not a hide. The timeout is unconditional rather than a `transitionend` — the drawer can close
     mid-animation, and that is precisely the case where the event never fires. */
  function collapse(node, done) {
    if (!node) { done(); return; }
    if (node.dataset.b2bmRemoving === '1') return;
    node.dataset.b2bmRemoving = '1';
    node.style.height = node.offsetHeight + 'px';
    node.style.marginTop = window.getComputedStyle(node).marginTop;
    void node.offsetHeight;
    node.classList.add('is-removing');
    node.style.height = '0px';
    node.style.marginTop = '0px';
    window.setTimeout(function () {
      /* 🔴 TAKE THE NODE OUT WHEN THE ANIMATION ENDS, NOT WHEN THE SERVER ANSWERS (2026-08-30).
         Operator: « the delay is quite slow, resulting in the size row remaining blank for a while
         before collapsing ». The collapse ran in 180 ms and then the row sat as a zero-height,
         zero-opacity GHOST until the repaint landed — half a second or more of a card whose head
         and totals still counted a line the buyer had just deleted. Removing it here settles the
         layout at once and the repaint merely confirms it. If the request fails, the `cart:refresh`
         in `send()`'s catch re-renders from the basket and the row comes back — the truth still
         wins, it just is not what we wait for. */
      removeNode(node);
      done();
    }, COLLAPSE_MS);
  }

  /* A group whose last size just went is an empty card: head, confirm block and all. Left alone it
     would linger until the repaint, which is the same staleness one level up. */
  function removeNode(node) {
    var group = node.closest('.b2bm__group, .b2bc__group');
    node.remove();
    if (group && !group.querySelector('.b2bm__row, .b2bc__row')) group.remove();
  }

  /* Keys, not line indices — the same rule the queue follows, for the same reason: zeroing line N
     renumbers every line after it, so a multi-line group removal driven by index removes the wrong
     rows on its second request. `update()` sends the whole group in ONE round-trip, behind anything
     already queued, so a removal can never overtake a quantity edit and land on a basket that has
     already moved. */
  function removeKeys(keys) {
    var updates = {};
    keys.forEach(function (k) { if (k) updates[k] = 0; });
    return update(updates);
  }

  function groupOf(el) { return el.closest('.b2bm__group, .b2bc__group'); }
  function headOf(g) { return g.querySelector('.b2bm__group-head, .b2bc__group-head'); }
  function confirmOf(g) { return g.querySelector('.b2bm__confirm, .b2bc__confirm'); }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var surface = event.target.closest('#mini-cart, .b2bc');
    if (!surface) return;

    /* ── row ✕ and the qty-1 trash: IMMEDIATE, no confirmation. 40b is explicit — one size is
       reversible at the stepper, so only the GROUP ✕ confirms. */
    var lineBtn = event.target.closest('[data-b2bm-remove]');
    if (lineBtn) {
      event.preventDefault();
      var key = lineBtn.getAttribute('data-b2bm-remove');
      collapse(lineBtn.closest('.b2bm__row, .b2bc__row'), function () { removeKeys([key]); });
      return;
    }

    /* ── group ✕ → the card header BECOMES the question, in place. Never a modal, never an
       undo-toast; the rows stay visible beneath it, greyed with their steppers frozen. */
    var groupBtn = event.target.closest('[data-b2bm-remove-group]');
    if (groupBtn) {
      event.preventDefault();
      var group = groupOf(groupBtn);
      if (!group) return;
      var head = headOf(group), box = confirmOf(group);
      if (head) head.hidden = true;
      if (box) box.hidden = false;
      group.classList.add('is-confirming');
      var yes = group.querySelector('[data-b2bm-confirm-yes]');
      if (yes) yes.focus();
      return;
    }

    var cancel = event.target.closest('[data-b2bm-confirm-no]');
    if (cancel) {
      event.preventDefault();
      var cGroup = groupOf(cancel);
      if (!cGroup) return;
      var cHead = headOf(cGroup), cBox = confirmOf(cGroup);
      if (cBox) cBox.hidden = true;
      if (cHead) cHead.hidden = false;
      cGroup.classList.remove('is-confirming');
      return;
    }

    var accept = event.target.closest('[data-b2bm-confirm-yes]');
    if (accept) {
      event.preventDefault();
      var aGroup = groupOf(accept);
      if (!aGroup) return;
      collapse(aGroup, function () {
        removeKeys((aGroup.getAttribute('data-b2bm-keys') || '').split(',').filter(Boolean));
      });
    }
  });

  /* 🔴 « Commander » IS THE OTHER WAY TO SAY « I AM DONE ». Clicking it with edits still queued
     flushes them first and then goes, so the checkout is always entered on the basket the buyer can
     see. `submitting` stops the re-dispatched click being intercepted again. */
  document.addEventListener('click', function (event) {
    if (submitting) return;
    if (!event.target.closest) return;
    var btn = event.target.closest('[name="checkout"]');
    if (!btn) return;
    if (!hasPending() && inFlight === 0) return;
    event.preventDefault();
    event.stopPropagation();
    flush().then(function () {
      submitting = true;
      var again = document.querySelector('[name="checkout"]');
      if (again) again.click();
      window.setTimeout(function () { submitting = false; }, 4000);
    });
  }, true);

  window.WajB2bCart = {
    queue: queue,
    flush: flush,
    update: update,
    hasPending: function () { return hasPending() || inFlight > 0; }
  };
})();
